/**
 * §7.4 登入方式與 SMTP。需要 `system.smtp`（SMTP）與 `system.auth`（登入方式開關）。
 *
 * ── 這一頁的核心是 §5.4 的三重啟用閘門 ──────────────────────────────────
 * Email 驗證碼登入不能隨便打開：沒有寄信主機就寄不出驗證碼，開了等於把人鎖在門外。
 * 所以後端要求三個條件同時成立才准啟用——
 *   1. SMTP 設定完整    2. 寄過測試信而且成功    3. 管理者明確打開開關
 * 畫面依後端回的 `emailCodeGate` 決定開關能不能動，**不自己推導**。
 *
 * 還有一個刻意的失效安全設計：改了 SMTP 的連線欄位（主機、埠、帳號、密碼）之後，
 * 「已驗證」會被清掉，而且 Email 驗證碼登入會**自動關閉**。這不是 bug，
 * 是為了避免「改壞了設定但開關還開著」——那會讓所有只能用驗證碼登入的人進不來。
 * 後端回的 `notice` 就是在講這件事，畫面要原樣顯示。
 */

import { useState } from 'react'
import { ApiError, api } from '../../api/client.js'
import { useQuery } from '../../api/hooks.js'
import { useAuth } from '../../auth/AuthContext.js'
import { Alert, Button, Card, Field, KV, Mono, Pill, Select, TextInput } from '../../components/ui.js'
import { ts } from '../../../../shared/lib/format.js'

interface Smtp {
  host: string | null
  port: number
  secure: boolean
  username: string | null
  hasPassword: boolean
  from_name: string
  from_email: string | null
  verified_at: string | null
  configured: boolean
}

interface AuthSettingsPayload {
  auth: { password: boolean; emailCode: boolean }
  smtp: Smtp
  emailCodeGate: { configured: boolean; verified: boolean; canEnable: boolean; hint: string | null }
}

export function AdminAuth() {
  const { can, me } = useAuth()
  const state = useQuery<AuthSettingsPayload>('/api/admin/auth-settings')
  const [notice, setNotice] = useState<{ tone: 'ok' | 'warn' | 'danger'; text: string } | null>(null)

  if (state.loading) return <p className="text-sm text-ink-500">載入中…</p>
  if (state.error) return <Alert tone="danger" title="讀不到設定">{state.error.message}</Alert>
  if (!state.data) return null

  const reload = () => { void state.reload() }

  return (
    <div className="space-y-4">
      {notice && (
        <Alert tone={notice.tone === 'ok' ? 'ok' : notice.tone === 'warn' ? 'warn' : 'danger'}>
          {notice.text}
        </Alert>
      )}

      <LoginMethods
        data={state.data}
        canEdit={can('system.auth')}
        onDone={(text, tone) => { setNotice({ tone, text }); reload() }}
      />

      {can('system.smtp') ? (
        <SmtpCard
          smtp={state.data.smtp}
          myEmail={me?.user.email ?? ''}
          onDone={(text, tone) => { setNotice({ tone, text }); reload() }}
        />
      ) : (
        <Card title="寄信設定（SMTP）">
          <Alert tone="info" title="唯讀">
            變更寄信設定需要 <Mono>system.smtp</Mono> 權限。
          </Alert>
        </Card>
      )}
    </div>
  )
}

// ── 登入方式 ────────────────────────────────────────────────────────────────

function LoginMethods({ data, canEdit, onDone }: {
  data: AuthSettingsPayload
  canEdit: boolean
  onDone: (text: string, tone: 'ok' | 'warn' | 'danger') => void
}) {
  const { auth, emailCodeGate } = data
  const [busy, setBusy] = useState(false)

  const save = async (next: { password: boolean; emailCode: boolean }) => {
    // §5.2 兩種都關掉就沒有人登得進來，包含你自己。這一層在後端也擋，
    // 但在這裡先擋掉，使用者不用送出去才知道不行。
    if (!next.password && !next.emailCode) {
      onDone('兩種登入方式不能同時關閉——關掉之後沒有人登得進來，包含您自己。', 'danger')
      return
    }
    setBusy(true)
    try {
      await api.put('/api/admin/auth-settings', next)
      onDone('登入方式已更新，並寫入認證稽核。', 'ok')
    } catch (e) {
      onDone(e instanceof ApiError ? e.message : String(e), 'danger')
    } finally { setBusy(false) }
  }

  return (
    <Card title="登入方式" subtitle="至少要保留一種，否則沒有人登得進來">
      <div className="space-y-3">
        <Method
          label="密碼登入"
          hint="預設開啟。密碼至少 12 個字，不能是常見密碼，也不能太像 Email 或姓名（§5.6）。"
          on={auth.password}
          disabled={!canEdit || busy}
          onToggle={(v) => void save({ ...auth, password: v })}
        />

        <Method
          label="Email 驗證碼登入"
          hint="6 位數字，有效 10 分鐘。開啟後使用者忘記密碼可以自助登入再重設，不必找管理者。"
          on={auth.emailCode}
          disabled={!canEdit || busy || (!auth.emailCode && !emailCodeGate.canEnable)}
          blockedReason={!auth.emailCode && !emailCodeGate.canEnable ? emailCodeGate.hint : null}
          onToggle={(v) => void save({ ...auth, emailCode: v })}
        />
      </div>

      {/* §5.4 三重閘門的狀態，讓人看得出卡在哪一關 */}
      <div className="mt-4 rounded-md border border-ink-200 bg-ink-50 p-3">
        <p className="mb-2 text-xs font-medium text-ink-600">Email 驗證碼的啟用條件</p>
        <ul className="space-y-1 text-sm">
          <Gate ok={emailCodeGate.configured} label="SMTP 設定完整" />
          <Gate ok={emailCodeGate.verified} label="測試信寄送成功" />
          <Gate ok={auth.emailCode} label="開關已打開" />
        </ul>
        {emailCodeGate.hint && (
          <p className="mt-2 text-xs text-amber-800">{emailCodeGate.hint}</p>
        )}
      </div>

      {!auth.emailCode && (
        <div className="mt-3">
          <Alert tone="info" title="目前只開放密碼登入">
            使用者忘記密碼時<strong>只能找系統管理者重設</strong>——登入畫面上不會有自助的入口。
            現場人數多的時候這會變成日常負擔，值得把 SMTP 設起來。
          </Alert>
        </div>
      )}
    </Card>
  )
}

function Method({ label, hint, on, disabled, blockedReason, onToggle }: {
  label: string
  hint: string
  on: boolean
  disabled: boolean
  blockedReason?: string | null
  onToggle: (v: boolean) => void
}) {
  return (
    <div className={`rounded-md border p-3 ${on ? 'border-emerald-300 bg-emerald-50' : 'border-ink-200'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-ink-900">
            {label}
            <span className="ml-2">{on ? <Pill tone="emerald">已開啟</Pill> : <Pill tone="ink">已關閉</Pill>}</span>
          </p>
          <p className="mt-0.5 text-xs text-ink-500">{hint}</p>
          {blockedReason && <p className="mt-1 text-xs text-amber-800">⚠ {blockedReason}</p>}
        </div>
        <Button
          size="sm"
          variant={on ? 'secondary' : 'primary'}
          disabled={disabled}
          onClick={() => onToggle(!on)}
        >{on ? '關閉' : '開啟'}</Button>
      </div>
    </div>
  )
}

function Gate({ ok, label }: { ok: boolean; label: string }) {
  return (
    <li className="flex items-center gap-2">
      <span className={ok ? 'text-emerald-600' : 'text-ink-300'}>{ok ? '✓' : '○'}</span>
      <span className={ok ? 'text-ink-700' : 'text-ink-400'}>{label}</span>
    </li>
  )
}

// ── SMTP ────────────────────────────────────────────────────────────────────

function SmtpCard({ smtp, myEmail, onDone }: {
  smtp: Smtp
  myEmail: string
  onDone: (text: string, tone: 'ok' | 'warn' | 'danger') => void
}) {
  const [form, setForm] = useState({
    host: smtp.host ?? '',
    port: String(smtp.port),
    secure: smtp.secure,
    username: smtp.username ?? '',
    password: '',
    from_name: smtp.from_name,
    from_email: smtp.from_email ?? '',
  })
  const [busy, setBusy] = useState(false)
  const [testTo, setTestTo] = useState(myEmail)
  const [testResult, setTestResult] = useState<
    { ok: boolean; error: string | null; via: string | null; messageId: string | null } | null
  >(null)
  const [error, setError] = useState<string | null>(null)

  const save = async () => {
    setBusy(true)
    setError(null)
    try {
      const r = await api.put<{ emailCodeDisabled: boolean; notice: string | null }>('/api/admin/smtp', {
        host: form.host.trim() || null,
        port: Number(form.port) || 587,
        secure: form.secure,
        username: form.username.trim() || null,
        // 空字串代表「不改密碼」，不是「清空密碼」——後端也是這樣約定的
        ...(form.password ? { password: form.password } : {}),
        from_name: form.from_name.trim(),
        from_email: form.from_email.trim() || null,
      })
      setForm((f) => ({ ...f, password: '' }))
      setTestResult(null)
      onDone(r.notice ?? '寄信設定已儲存。', r.emailCodeDisabled ? 'warn' : 'ok')
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
    } finally { setBusy(false) }
  }

  const test = async () => {
    setBusy(true)
    setError(null)
    setTestResult(null)
    try {
      const r = await api.post<{
        ok: boolean; error: string | null; via: string | null; messageId: string | null
      }>('/api/admin/smtp/test', { to: testTo.trim() })
      setTestResult(r)
      // 刻意不說「已寄到 xxx」——SMTP 成功只代表**那台伺服器收下了**，
      // 不代表信會進到對方的收件匣。說滿了會讓人把「沒收到」誤判成系統壞掉。
      if (r.ok) onDone(`${r.via ?? '寄信主機'} 已收下這封信。確認收得到之後就可以開啟 Email 驗證碼登入。`, 'ok')
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
    } finally { setBusy(false) }
  }

  return (
    <Card
      title="寄信設定（SMTP）"
      subtitle="只用來寄登入驗證碼。系統不會寄任何其他信"
      tone={smtp.configured && smtp.verified_at ? 'ok' : 'plain'}
    >
      <div className="mb-3">
        <KV items={[
          ['目前狀態', smtp.configured
            ? (smtp.verified_at
                ? <span key="v"><Pill tone="emerald">已驗證</Pill> <span className="text-xs text-ink-500">{ts(smtp.verified_at)}</span></span>
                : <Pill key="n" tone="amber">已設定，尚未測試</Pill>)
            : <Pill key="x" tone="ink">尚未設定</Pill>],
          ['密碼', smtp.hasPassword ? '已儲存（加密）' : '未設定'],
        ]} />
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="主機" hint="例如 smtp.office365.com" required>
          <TextInput value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} />
        </Field>
        <Field label="埠" hint="587 = STARTTLS（常見）；465 = SSL">
          <TextInput inputMode="numeric" value={form.port}
            onChange={(e) => setForm({ ...form, port: e.target.value.replace(/\D/g, '') })} />
        </Field>
        <Field label="連線加密">
          <Select value={form.secure ? 'ssl' : 'starttls'}
            onChange={(e) => setForm({ ...form, secure: e.target.value === 'ssl' })}>
            <option value="starttls">STARTTLS（埠 587）</option>
            <option value="ssl">SSL／TLS（埠 465）</option>
          </Select>
        </Field>
        <Field label="帳號" hint="留空表示不需要驗證">
          <TextInput value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
        </Field>
        <Field label="密碼" hint={smtp.hasPassword ? '留空 = 沿用已儲存的密碼' : '會加密後存在資料庫'}>
          <TextInput type="password" value={form.password} autoComplete="new-password"
            placeholder={smtp.hasPassword ? '（不變）' : ''}
            onChange={(e) => setForm({ ...form, password: e.target.value })} />
        </Field>
        <Field label="寄件者名稱">
          <TextInput value={form.from_name} onChange={(e) => setForm({ ...form, from_name: e.target.value })} />
        </Field>
        <Field label="寄件者信箱" required>
          <TextInput type="email" value={form.from_email}
            onChange={(e) => setForm({ ...form, from_email: e.target.value })} />
        </Field>
      </div>

      {/* §5.4 的失效安全設計要在按下去之前就講清楚 */}
      <div className="mt-3">
        <Alert tone="warn" title="改了連線設定會自動關閉 Email 驗證碼登入">
          主機、埠、帳號、密碼任何一項變更之後，「已驗證」會被清掉，而且 Email 驗證碼登入會
          <strong>自動關閉</strong>。這是刻意的——改壞了設定但開關還開著的話，
          只能用驗證碼登入的人會全部進不來。重新寄一次測試信通過後再開回來。
        </Alert>
      </div>

      {error && <div className="mt-3"><Alert tone="danger">{error}</Alert></div>}

      <div className="mt-4 flex flex-wrap gap-2">
        <Button variant="primary" disabled={busy} onClick={() => void save()}>
          {busy ? '請稍候…' : '儲存設定'}
        </Button>
      </div>

      <div className="mt-4 border-t border-ink-200 pt-4">
        <p className="mb-2 text-sm font-medium text-ink-800">寄一封測試信</p>
        <p className="mb-2 text-xs text-ink-500">
          寄成功才會標記為「已驗證」，也才能開啟 Email 驗證碼登入。先按上面的「儲存設定」再測試。
        </p>
        <div className="flex flex-wrap items-end gap-2">
          <Field label="寄到" className="min-w-[240px]">
            <TextInput type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} />
          </Field>
          <Button disabled={busy || !testTo.trim()} onClick={() => void test()}>
            {busy ? '寄送中…' : '寄送測試信'}
          </Button>
        </div>

        {testResult && (
          <div className="mt-3">
            {testResult.ok ? (
              <Alert tone="ok" title={`${testResult.via ?? '寄信主機'} 收下了這封信`}>
                <p>
                  SMTP 交握與投遞都成功，收件匣應該會有一封主旨含測試字樣的信。
                  {testResult.messageId && (
                    <span className="block text-[11px] text-ink-500">
                      Message-ID <Mono className="text-[11px]">{testResult.messageId}</Mono>
                    </span>
                  )}
                </p>
                {/*
                  這一段是實際踩過的坑：開發環境把 SMTP 指到 mailpit（攔信桶），
                  畫面顯示成功但信永遠不會送出去，看起來就像系統在騙人。
                  「成功」只保證那台伺服器收下了，不保證信會到對方手上。
                */}
                <p className="mt-2 text-sm">
                  <strong>沒收到的話先確認寄到哪裡去了。</strong>「成功」的意思是
                  <strong>{testResult.via ?? '那台主機'} 收下了這封信</strong>，
                  不代表它會往外送——開發或測試環境常常指到攔信桶（例如 mailpit），
                  那種主機會照單全收但不投遞。垃圾信匣也看一下。
                </p>
              </Alert>
            ) : (
              <Alert tone="danger" title="寄送失敗">
                {/* §5.4：這是管理者畫面，完整的錯誤訊息是排錯的主要線索，不要遮 */}
                <p className="font-mono text-xs break-all">{testResult.error}</p>
                <p className="mt-2 text-sm">
                  常見原因：主機或埠填錯、帳號密碼不對、寄件者信箱沒有被寄信主機授權、
                  或防火牆擋掉了對外的 SMTP 連線。
                </p>
              </Alert>
            )}
          </div>
        )}
      </div>
    </Card>
  )
}
